import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import handleClearError from "../../../core/methods/handleClearError"; 
import ErrorMessage from "../../../core/common/ErrorMessage";
import useApi from "../../../core/customhook/useApi";
import webservice_url from "../../../core/webservice/Webservice_url";
import { toast } from "react-toastify";
import logosvg from "../../../images/logo.svg";
// ── Types ──────────────────────────────────────────────────────────────────────

interface ForgotFormType {
  email: string;
  otp: string;
}

// ── Dummy OTP (remove in production) ──────────────────────────────────────────
const MOCK_OTP = "445566";

// ── Component ──────────────────────────────────────────────────────────────────

const ForgotPassword = () => {
  const routes = all_routes;
  const navigate = useNavigate();

  const [formData, setFormData] = useState<ForgotFormType>({
    email: "",
    otp: "",
  });
  const [errors, setErrors] = useState<Record<string, string> | null>(null);
  const [step, setStep] = useState<"email" | "otp">("email");

  // countdown timer
  const [timeLeft, setTimeLeft] = useState(0);
  const seconds = Math.floor((timeLeft / 1000) % 60);

  // API
  const forgotPasswordAPI = useApi<any>(webservice_url.forgot_password, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });
  const resendOtpAPI = useApi<any>(webservice_url.resend_otp, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });
  const verifyOtpAPI = useApi<any>(webservice_url.verify_forgot_otp, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const loading_forgot = forgotPasswordAPI.loading;
  const loading_resend = resendOtpAPI.loading;
  const loading_verify = verifyOtpAPI.loading;

  // ── helpers ──────────────────────────────────────────────────────────────────
  const handleTimer = () => {
    const expireTime = Date.now() + 30 * 1000;
    const timer = setInterval(() => {
      const remaining = expireTime - Date.now();
      if (remaining <= 0) {
        clearInterval(timer);
        setTimeLeft(0);
      } else {
        setTimeLeft(remaining);
      }
    }, 1000);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value?.trim() }));
    handleClearError(setErrors, name);
  };

  // ── validation ────────────────────────────────────────────────────────────────

  const validateEmail = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)
    ) {
      newErrors.email = "Enter a valid email address";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateOtp = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.otp?.trim()) {
      newErrors.otp = "Please enter OTP";
    }
    // else if (formData.otp !== MOCK_OTP) {
    //   newErrors.otp = "Invalid OTP";
    // }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ---- handleSendOtp ----
  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateEmail()) return;

    try {
      const res = await forgotPasswordAPI.callApi({
        email: formData?.email ?? "",
      });
      if (res?.success) {
        setStep("otp");
        handleTimer();
      }
    } catch (error: any) {
      console.error("login failed:", error?.response);
      const errorMsg = error?.response?.data?.error ?? "Invalid Credentials";
      toast.error(errorMsg);
    }
  };

  // ---- handle resend otp ----
  const handleResendOtp = async () => {
    try {
      const res = await resendOtpAPI.callApi({ email: formData?.email });
      if (res?.success) {
        toast.success(res?.message || "Sent Successfully");
        handleTimer();
      }
    } catch (error: any) {
      console.error("Error:", error.response);
    }
  };

  // ---- handle otp verify  ----
  const handleOTPVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateOtp()) return;

    try {
      const res = await verifyOtpAPI.callApi({
        email: formData?.email ?? "",
        otp: formData?.otp?.toString() ?? "",
      });
      if (res?.success) {
        localStorage.setItem("user-data", formData?.email);
        navigate(routes.resetPassword, { replace: true });
      }
    } catch (error: any) {
      console.error("Error:", error?.response);
      const errorMsg = error?.response?.data?.error ?? "Invalid OTP";
      toast.error(errorMsg);
    }
  };

  // ── render ────────────────────────────────────────────────────────────────────

  return (
    <>
      <div className="container-fluid p-0">
        <div className="row g-0 vh-100">
          <div className="col-lg-7 d-none d-lg-block">
            <div
              className="vh-100 position-relative overflow-hidden"
              style={{
                background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
              }}
            >
              {/* CONTENT */}
              <div
                className="position-absolute top-50 start-50 translate-middle text-center text-white px-2"
                style={{ zIndex: 2 }}
              >
                  <img
                                  src={logosvg}
                                  alt="logo"
                                  className="img-fluid mb-4"
                                  style={{ maxWidth: "220px" }}
                                />

                <h1
                  className="fw-bold mb-4"
                  style={{ fontSize: "28px", color: "white" }}
                >
                  Welcome to the Auperty Services Private Limited
                </h1>

                <p
                  className="text-light"
                  style={{ fontSize: "18px", lineHeight: "32px" }}
                >
                  Sign in to access your background verification application,
                  upload required documents, track verification progress, and
                  complete your onboarding seamlessly.
                </p>
              </div>

              {/* SCROLLING IMAGES */}
              <div className="scroll-images">
                <img
                  src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d"
                  alt=""
                />
                <img
                  src="https://images.unsplash.com/photo-1552664730-d307ca884978"
                  alt=""
                />
                <img
                  src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3"
                  alt=""
                />
                <img
                  src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f"
                  alt=""
                />
              </div>
            </div>
          </div>
          <div className="col-lg-5 col-md-12"> 
            <div className="container-fluid d-flex align-items-center justify-content-center bg-light p-5">
              <div className="w-100" style={{ maxWidth: 600 }}>
                <form className="">
                  <div className="d-flex flex-column justify-content-cente align-items-center">
                    <div className="mx-auto mb-3 text-center">
                      <img
                        src="/assets/img/logo/AupTech.png"
                        className="img-fluid"
                        alt="Logo"
                        style={{ maxWidth: "170px" }}
                      />
                    </div>

                    <div>
                      <div className="text-center">
                        <div className="text-center mb-4">
                          <h2 className="forgot-title mb-2">
                            Forgot Password?
                          </h2>
                          <p className="forgot-subtext mb-0">
                            No worries — enter your email and we'll send you a
                            code to reset your password.
                          </p>
                        </div>
                        {/* <p className="mb-0 mt-2">
                          {step === "email" ? (
                            "Enter your registered email to receive an OTP"
                          ) : step === "otp" ? (
                            <>
                              A 6-digit OTP has been sent to&nbsp; "
                              <strong>{formData.email}</strong>"
                            </>
                          ) : (
                            ""
                          )}
                        </p> */}
                      </div>

                      {/* Email field */}
                      <div className="mb-3">
                        <label className="form-label">Email Address</label>
                        <div className="input-group mb-1">
                          <input
                            type="email"
                            className="form-control border-end-0"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            disabled={step === "otp" || loading_forgot}
                            placeholder="Enter your email"
                          />
                          <span className="input-group-text border-start-0">
                            <i className="ti ti-mail" />
                          </span>
                        </div>
                        <ErrorMessage message={errors?.email} />
                      </div>

                      {/* OTP field — visible only after email step */}
                      {step === "otp" && (
                        <>
                          <div className="mb-2">
                            <label className="form-label">Enter OTP</label>

                            <input
                              type="text"
                              inputMode="numeric"
                              className="form-control mb-1"
                              name="otp"
                              value={formData.otp}
                              placeholder="Enter 6-digit OTP"
                              disabled={loading_resend || loading_verify}
                              onChange={(e) => {
                                const val = e.target.value
                                  .replace(/\D/g, "")
                                  .slice(0, 6);
                                setFormData((prev) => ({ ...prev, otp: val }));
                                handleClearError(setErrors, "otp");
                              }}
                            />
                            <ErrorMessage message={errors?.otp} />
                          </div>

                          {/* Timer + Resend */}
                          <div
                            className={`d-flex mb-3 ${
                              !!timeLeft
                                ? "justify-content-between"
                                : "justify-content-end"
                            } px-1`}
                          >
                            {!!timeLeft && (
                              <div className="text-muted small">
                                Resend OTP in {seconds} sec
                              </div>
                            )}
                            <button
                              type="button"
                              onClick={handleResendOtp}
                              className="btn border-0 bg-transparent p-0 link-info small"
                              disabled={
                                !!timeLeft || loading_resend || loading_verify
                              }
                            >
                              {loading_resend ? (
                                "Sending OTP..."
                              ) : (
                                <>
                                  <i className="ti ti-refresh"></i> Resend OTP
                                </>
                              )}
                            </button>
                          </div>
                        </>
                      )}

                      {/* Submit button */}
                      <div className="mb-3 mt-0">
                        {step === "email" ? (
                          <button
                            type="submit"
                            className="btn btn-primary w-100 "
                            onClick={handleSendOtp}
                            disabled={loading_forgot}
                          >
                            {loading_forgot ? "Sending OTP…" : "Send OTP"}
                          </button>
                        ) : (
                          <button
                            type="submit"
                            className="btn btn-primary w-100 py1 fs15"
                            onClick={handleOTPVerify}
                            disabled={loading_verify}
                          >
                            {loading_verify
                              ? "Verifying…"
                              : "Verify & Continue"}
                          </button>
                        )}
                      </div>

                      {/* Back to login */}
                      <div className="text-center">
                        <Link
                          to={all_routes.login}
                          className="link-secondary small"
                        >
                          <i className="ti ti-arrow-left" /> Back to Sign In
                        </Link>
                      </div>
                    </div>
                    {/* 
                    <div className="mt-3 pb-4 text-center">
                      <p className="mb-0 text-gray-9">
                        © 2026 Aupertz. All rights reserved.
                      </p>
                    </div> */}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;
